import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Download,
  Loader2,
  AlertCircle,
  Users,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Filter,
  X
} from "lucide-react";
import {
  searchAllContacts,
  fetchMailingStats,
  exportContacts,
  formatAddress,
  formatCurrency,
  COUNTIES,
  POSTCARD_SIZES
} from "../../services/MailingListService";
import ContactStatusBadge from "./ContactStatusBadge";

/**
 * GlobalSearchView - Search across all mailing lists with county/city filters
 */
const GlobalSearchView = ({ userEmail, userMeta }) => {
  // Stats
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [pageKey, setPageKey] = useState(null);
  const [nextPageKey, setNextPageKey] = useState(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    county: "",
    city: "",
    zip: "",
    status: "",
    has_phone: false
  });

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Load stats on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoadingStats(true);
        const data = await fetchMailingStats();
        setStats(data);
      } catch (err) {
        console.error("Failed to load stats:", err);
      } finally {
        setIsLoadingStats(false);
      }
    };
    loadStats();
  }, []);

  // Search function
  const performSearch = useCallback(async (query, filterOpts, pageKeyVal = null) => {
    try {
      setIsSearching(true);
      setError(null);

      const searchFilters = {
        q: query || undefined,
        county: filterOpts.county || undefined,
        city: filterOpts.city || undefined,
        zip: filterOpts.zip || undefined,
        status: filterOpts.status || undefined,
        has_phone: filterOpts.has_phone || undefined,
        limit: 100,
        page_key: pageKeyVal || undefined
      };

      const result = await searchAllContacts(searchFilters);
      setContacts(result.contacts || []);
      setTotalCount(result.total || 0);
      setNextPageKey(result.next_page_key || null);
      setPageKey(pageKeyVal);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search on query change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchQuery || filters.county || filters.city || filters.status) {
        performSearch(searchQuery, filters);
      } else {
        setContacts([]);
        setTotalCount(0);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, filters, performSearch]);

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await exportContacts(null, {
        county: filters.county || undefined
      });

      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to export contacts: " + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  // Clear a single filter
  const clearFilter = (filterKey) => {
    setFilters((prev) => ({ ...prev, [filterKey]: filterKey === "has_phone" ? false : "" }));
  };

  // Get active filter count
  const activeFilterCount = Object.entries(filters).filter(
    ([key, val]) => (key === "has_phone" ? val : val !== "")
  ).length;

  // Calculate total cost estimate
  const totalContacts = stats?.total_contacts || 0;
  const estimatedCost = totalContacts * POSTCARD_SIZES[0].price;

  return (
    <div className="p-6">
      {/* County Stats Cards */}
      {!isLoadingStats && stats && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-indigo-500" />
            Contact Statistics by County
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(stats.by_county || {}).map(([county, data]) => (
              <div
                key={county}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold dark:text-white text-slate-900">
                      {county} County
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {COUNTIES.find((c) => c.id === county)?.metro || "Texas"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Contacts</span>
                    <span className="font-medium dark:text-white text-slate-900">
                      {(data.contacts || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Verified</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {(data.verified || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Sent</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {(data.sent || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFilters((prev) => ({ ...prev, county }));
                    setShowFilters(true);
                  }}
                  className="mt-4 w-full text-sm text-sky-600 dark:text-sky-400 hover:text-sky-500 font-medium"
                >
                  Search {county} County →
                </button>
              </div>
            ))}

            {/* Total card */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl p-5">
              <h3 className="font-semibold text-lg mb-4">Total Contacts</h3>
              <div className="text-3xl font-bold mb-2">
                {totalContacts.toLocaleString()}
              </div>
              <div className="text-sm opacity-90 mb-4">
                Est. 4×6 Postcard Cost: {formatCurrency(estimatedCost)}
              </div>
              <div className="flex gap-4 text-sm">
                <div>
                  <div className="opacity-75">Lists</div>
                  <div className="font-semibold">{stats.total_lists || 0}</div>
                </div>
                <div>
                  <div className="opacity-75">Pending</div>
                  <div className="font-semibold">
                    {(stats.by_status?.pending || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="opacity-75">Sent</div>
                  <div className="font-semibold">
                    {(stats.by_status?.sent || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-indigo-500" />
          Search All Contacts
        </h2>

        {/* Search Input */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={`Search ${totalContacts.toLocaleString()} contacts by name, address, or phone...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border rounded-xl flex items-center gap-2 font-medium transition-colors ${
              showFilters || activeFilterCount > 0
                ? "border-sky-500 text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20"
                : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-sky-600 text-white text-xs flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || totalCount === 0}
            className="px-4 py-3 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl flex items-center gap-2 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export CSV
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl mb-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  County
                </label>
                <select
                  value={filters.county}
                  onChange={(e) => setFilters((prev) => ({ ...prev, county: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
                >
                  <option value="">All Counties</option>
                  {COUNTIES.map((county) => (
                    <option key={county.id} value={county.id}>
                      {county.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  City
                </label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => setFilters((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="e.g., Houston"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={filters.zip}
                  onChange={(e) => setFilters((prev) => ({ ...prev, zip: e.target.value }))}
                  placeholder="e.g., 77002"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.has_phone}
                  onChange={(e) => setFilters((prev) => ({ ...prev, has_phone: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                />
                Has phone number
              </label>
              {activeFilterCount > 0 && (
                <button
                  onClick={() =>
                    setFilters({ county: "", city: "", zip: "", status: "", has_phone: false })
                  }
                  className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Active Filters Pills */}
        {activeFilterCount > 0 && !showFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.county && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm">
                County: {filters.county}
                <button onClick={() => clearFilter("county")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {filters.city && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm">
                City: {filters.city}
                <button onClick={() => clearFilter("city")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {filters.zip && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm">
                ZIP: {filters.zip}
                <button onClick={() => clearFilter("zip")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm">
                Status: {filters.status}
                <button onClick={() => clearFilter("status")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {filters.has_phone && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm">
                Has phone
                <button onClick={() => clearFilter("has_phone")}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Results */}
      {isSearching ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
        </div>
      ) : contacts.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Found {totalCount.toLocaleString()} contacts
            </p>
          </div>

          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    County
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <span className="font-medium dark:text-white text-slate-900">
                        {contact.name || "—"}
                      </span>
                      {contact.phone && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {contact.phone}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                      {formatAddress(contact) || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                      {contact.county || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <ContactStatusBadge status={contact.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(pageKey || nextPageKey) && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => performSearch(searchQuery, filters, null)}
                disabled={!pageKey}
                className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center gap-2 disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                First
              </button>
              <button
                onClick={() => performSearch(searchQuery, filters, nextPageKey)}
                disabled={!nextPageKey}
                className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center gap-2 disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      ) : searchQuery || activeFilterCount > 0 ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            No contacts found
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Try adjusting your search or filters
          </p>
        </div>
      ) : (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            Search your contacts
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Enter a name, address, or phone number to search across all mailing lists
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchView;
